import { z } from 'zod';
import { timesheetsBillingCodeSchema, timesheetsClockBreakTypeSchema, timesheetsClockCommandIdSchema, timesheetsEntryKindSchema, timesheetsTimeEntryIdSchema, } from './timesheets-clock.contract.js';
import { timesheetsEntrySchema } from './timesheets-entry.contract.js';
export const timesheetsEntrySplitCapability = 'timesheets.entry-split.v1';
export const timesheetsEntrySplitMinimumSegments = 2;
export const timesheetsEntrySplitMaximumSegments = 20;
export const timesheetsEntrySplitSegmentSchema = z.strictObject({
    entryKind: timesheetsEntryKindSchema,
    breakType: timesheetsClockBreakTypeSchema.nullable().default(null),
    billingCode: timesheetsBillingCodeSchema,
    workReferenceId: z.string().uuid().nullable().default(null),
    notes: z.string().trim().max(2_000).nullable().default(null),
    clockInOccurredAt: z.string().datetime({ offset: true }),
    clockOutOccurredAt: z.string().datetime({ offset: true }),
}).superRefine((segment, context) => {
    const clockIn = new Date(segment.clockInOccurredAt).getTime();
    const clockOut = new Date(segment.clockOutOccurredAt).getTime();
    if (clockOut <= clockIn) {
        context.addIssue({
            code: 'custom',
            path: ['clockOutOccurredAt'],
            message: 'Each split segment must end after it begins.',
        });
    }
    if ((clockOut - clockIn) % 1_000 !== 0) {
        context.addIssue({
            code: 'custom',
            path: ['clockOutOccurredAt'],
            message: 'Split segment durations must resolve to exact whole seconds.',
        });
    }
    if (segment.entryKind === 'break') {
        if (segment.breakType === null) {
            context.addIssue({ code: 'custom', path: ['breakType'], message: 'A break type is required.' });
        }
        if (segment.billingCode !== 'break') {
            context.addIssue({ code: 'custom', path: ['billingCode'], message: 'Break segments use break billing.' });
        }
        if (segment.workReferenceId !== null) {
            context.addIssue({ code: 'custom', path: ['workReferenceId'], message: 'Break segments cannot reference a Project.' });
        }
    }
    else {
        if (segment.breakType !== null) {
            context.addIssue({ code: 'custom', path: ['breakType'], message: 'Work segments cannot have a break type.' });
        }
        if (segment.billingCode === 'break') {
            context.addIssue({ code: 'custom', path: ['billingCode'], message: 'Work segments cannot use break billing.' });
        }
        if (segment.workReferenceId === null && (segment.notes?.length ?? 0) === 0) {
            context.addIssue({ code: 'custom', path: ['notes'], message: 'Notes are required for shop time.' });
        }
    }
});
export const timesheetsSplitEntrySchema = z.strictObject({
    commandId: timesheetsClockCommandIdSchema,
    expectedRevision: z.number().int().positive(),
    reason: z.string().trim().min(5).max(2_000),
    segments: z.array(timesheetsEntrySplitSegmentSchema)
        .min(timesheetsEntrySplitMinimumSegments)
        .max(timesheetsEntrySplitMaximumSegments),
}).superRefine((command, context) => {
    let priorEnd = null;
    for (const [index, segment] of command.segments.entries()) {
        const segmentStart = new Date(segment.clockInOccurredAt).getTime();
        if (priorEnd !== null && segmentStart !== priorEnd) {
            context.addIssue({
                code: 'custom',
                path: ['segments', index, 'clockInOccurredAt'],
                message: 'Split segments must be contiguous with no overlap or unassigned gap.',
            });
        }
        priorEnd = new Date(segment.clockOutOccurredAt).getTime();
    }
    const first = command.segments[0];
    const last = command.segments.at(-1);
    if (first !== undefined && last !== undefined &&
        new Date(last.clockOutOccurredAt).getTime() - new Date(first.clockInOccurredAt).getTime() >
            24 * 60 * 60 * 1_000) {
        context.addIssue({
            code: 'custom',
            path: ['segments'],
            message: 'A split cannot cover more than 24 hours.',
        });
    }
});
export const timesheetsEntrySplitResultSegmentSchema = z.strictObject({
    segmentIndex: z.number().int().min(1).max(timesheetsEntrySplitMaximumSegments),
    entry: timesheetsEntrySchema,
    payBasisStatus: z.literal('available'),
});
export const timesheetsEntrySplitResponseSchema = z.strictObject({
    originalTimeEntryId: timesheetsTimeEntryIdSchema,
    segments: z.array(timesheetsEntrySplitResultSegmentSchema)
        .min(timesheetsEntrySplitMinimumSegments)
        .max(timesheetsEntrySplitMaximumSegments),
    totalDurationSeconds: z.number().int().positive(),
    idempotent: z.boolean(),
});
//# sourceMappingURL=timesheets-entry-split.contract.js.map